const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'a',
  'img',
  'div',
  'span',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'blockquote',
  'pre',
  'code',
  'hr',
  'sub',
  'sup',
  'figure',
  'figcaption',
  'section',
  'article',
  'style',
  'picture',
  'video',
  'audio',
  'source',
  'track',
]

const ALLOWED_ATTRIBUTES = [
  'href',
  'src',
  'alt',
  'title',
  'class',
  'style',
  'target',
  'rel',
  'id',
  'colspan',
  'rowspan',
  'width',
  'height',
  'align',
  'controls',
  'poster',
  'srcset',
  'sizes',
  'media',
  'loading',
  'decoding',
  'fetchpriority',
  'autoplay',
  'muted',
  'loop',
  'playsinline',
  'webkit-playsinline',
  'x5-playsinline',
  'x5-video-player-type',
  'preload',
  'crossorigin',
  'referrerpolicy',
  'type',
  'kind',
  'srclang',
  'label',
]

const DROP_WITH_CONTENT_TAGS = new Set([
  'script',
  'iframe',
  'object',
  'embed',
  'link',
  'meta',
  'base',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'svg',
  'math',
])

/**
 * @description Clean CSS used by rich text style blocks and inline style attributes.
 * @param {string} css - Raw CSS text.
 * @returns {string} CSS with active import/script vectors removed.
 */
const sanitizeCssText = (css) => {
  return String(css || '')
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/@import[^;{}]*(?:;|$)/gi, '')
    .replace(
      /[^{};]*(?:expression\s*\(|behaviou?r\s*:|(?:java|vb)script\s*:|data\s*:)[^{};]*(?:;|$)/gi,
      ''
    )
}

/**
 * @description Preserve style tags while cleaning their CSS content.
 * @param {string} html - Raw HTML text.
 * @returns {string} HTML with sanitized style blocks.
 */
const sanitizeStyleBlocks = (html) => {
  return String(html || '').replace(
    /<\s*style\b[^>]*>([\s\S]*?)<\s*\/\s*style\s*>/gi,
    (_, css) => `<style>${sanitizeCssText(css)}</style>`
  )
}

const normalizeUrlForScheme = (value) =>
  String(value || '')
    .trim()
    .replace(/[\u0000-\u001F\u007F\s]+/g, '')
    .toLowerCase()

const isSafeResourceUrl = (value, options = {}) => {
  const normalized = normalizeUrlForScheme(value)
  if (!normalized) return false
  if (normalized.startsWith('javascript:') || normalized.startsWith('vbscript:')) return false
  if (normalized.startsWith('data:')) {
    return options.allowData === true && /^data:(image|video|audio)\//i.test(normalized)
  }
  return (
    normalized.startsWith('http://') ||
    normalized.startsWith('https://') ||
    normalized.startsWith('//') ||
    normalized.startsWith('/') ||
    normalized.startsWith('./') ||
    normalized.startsWith('../') ||
    normalized.startsWith('#')
  )
}

const sanitizeSrc = (src) => {
  return isSafeResourceUrl(src, { allowData: true }) ? src : ''
}

/**
 * @description Split srcset candidates without breaking commas inside URL query values (e.g. OSS `format,avif`).
 * @param {string} srcset - Raw srcset attribute value.
 * @returns {string[]} Candidate strings.
 */
export const splitSrcsetCandidates = (srcset) => {
  const raw = String(srcset || '').trim()
  if (!raw) return []

  const candidates = []
  let current = ''

  for (let i = 0; i < raw.length; i += 1) {
    const char = raw[i]
    if (char !== ',') {
      current += char
      continue
    }

    const rest = raw.slice(i + 1).trimStart()
    if (/^(?:https?:\/\/|\/\/|\/|\.\.?\/)/i.test(rest)) {
      if (current.trim()) candidates.push(current.trim())
      current = ''
      continue
    }

    current += char
  }

  if (current.trim()) candidates.push(current.trim())
  return candidates
}

const sanitizeSrcset = (srcset) => {
  return splitSrcsetCandidates(srcset)
    .map((candidate) => {
      const trimmed = candidate.trim()
      if (!trimmed) return ''
      const [url, ...descriptors] = trimmed.split(/\s+/)
      const safeUrl = sanitizeSrc(url)
      return safeUrl ? [safeUrl, ...descriptors].join(' ') : ''
    })
    .filter(Boolean)
    .join(', ')
}

/**
 * @description Check whether an attribute is safe for a rich-text element.
 * @param {string} name - Attribute name.
 * @param {string} tagName - Lowercase element tag name.
 * @returns {boolean} Whether the attribute may be retained.
 * @reason Legacy Word/TinyMCE documents use `<a name="...">` as same-page anchor targets.
 */
export const isAllowedRichTextAttribute = (name, tagName = '') => {
  const normalizedName = String(name || '').toLowerCase()
  const normalizedTagName = String(tagName || '').toLowerCase()

  return (
    ALLOWED_ATTRIBUTES.includes(normalizedName) ||
    normalizedName.startsWith('data-') ||
    normalizedName.startsWith('aria-') ||
    (normalizedTagName === 'a' && normalizedName === 'name')
  )
}

/**
 * @description 解析富文本链接的打开方式
 * @param {string} href - 链接地址
 * @returns {{target: string|null, rel: string|null}} 链接的 target 与 rel 属性
 * @reason 页内锚点必须在当前页面跳转，外部及站内页面链接沿用新窗口打开策略
 */
export const resolveRichTextAnchorAttributes = (href) => {
  const normalizedHref = String(href || '').trim()

  if (normalizedHref.startsWith('#')) {
    return {
      target: null,
      rel: null,
    }
  }

  return {
    target: '_blank',
    rel: 'noopener noreferrer',
  }
}

/**
 * @description Resolve the standards-based id for a legacy named anchor.
 * @param {string} name - Legacy anchor name.
 * @param {string} id - Existing anchor id.
 * @returns {string} Existing id or the normalized legacy anchor name.
 * @reason Nuxt route scrolling resolves fragment targets by id, while imported documents may only set name.
 */
export const resolveLegacyNamedAnchorId = (name, id = '') => {
  const normalizedId = String(id || '').trim()
  if (normalizedId) return normalizedId
  return String(name || '').trim()
}

/**
 * @description Sanitize HTML for rich text rendering.
 * @param {string} html - Raw HTML.
 * @returns {string} Sanitized HTML.
 * @reason Keep backend product detail HTML renderable while removing script vectors.
 * @edge-cases Falls back to regex sanitation during SSR where DOM APIs are unavailable.
 */
export const sanitizeHtml = (html) => {
  const raw = typeof html === 'string' ? html : ''
  if (!raw) return ''

  if (!import.meta.client) {
    return sanitizeStyleBlocks(raw)
      .replace(/<\s*script[^>]*>[\s\S]*?<\s*\/\s*script\s*>/gi, '')
      .replace(/\son[a-z]+\s*=\s*(['"]).*?\1/gi, '')
      .replace(/(href|src)\s*=\s*(['"])\s*javascript:[\s\S]*?\2/gi, '$1=$2$2')
  }

  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = sanitizeStyleBlocks(raw)

  const walk = (node) => {
    if (node.nodeType === 1) {
      const tagName = node.tagName.toLowerCase()
      if (!ALLOWED_TAGS.includes(tagName)) {
        const parent = node.parentNode
        if (DROP_WITH_CONTENT_TAGS.has(tagName)) {
          parent.removeChild(node)
          return
        }
        while (node.firstChild) {
          parent.insertBefore(node.firstChild, node)
        }
        parent.removeChild(node)
        return
      }

      const attrs = Array.from(node.attributes)
      attrs.forEach((attr) => {
        const name = attr.name.toLowerCase()
        if (!isAllowedRichTextAttribute(name, tagName)) {
          node.removeAttribute(attr.name)
          return
        }

        if (name === 'style') {
          const safeStyle = sanitizeCssText(attr.value)
          if (safeStyle.trim()) {
            node.setAttribute(attr.name, safeStyle)
          } else {
            node.removeAttribute(attr.name)
          }
        }
      })

      if (tagName === 'style') {
        node.textContent = sanitizeCssText(node.textContent || '')
        return
      }

      if (tagName === 'a' && node.getAttribute('href')) {
        const href = node.getAttribute('href')
        if (
          !href.startsWith('http://') &&
          !href.startsWith('https://') &&
          !href.startsWith('/') &&
          !href.startsWith('#')
        ) {
          node.setAttribute('href', '#')
        }

        const { target, rel } = resolveRichTextAnchorAttributes(node.getAttribute('href'))
        if (target) node.setAttribute('target', target)
        else node.removeAttribute('target')
        if (rel) node.setAttribute('rel', rel)
        else node.removeAttribute('rel')
      }

      if (tagName === 'a' && node.getAttribute('name')) {
        const anchorId = resolveLegacyNamedAnchorId(
          node.getAttribute('name'),
          node.getAttribute('id')
        )
        if (anchorId) node.setAttribute('id', anchorId)
      }

      if (tagName === 'img' && node.getAttribute('src')) {
        const safe = sanitizeSrc(node.getAttribute('src'))
        if (!safe) node.setAttribute('src', '')
      }

      if ((tagName === 'img' || tagName === 'source') && node.getAttribute('srcset')) {
        const safe = sanitizeSrcset(node.getAttribute('srcset'))
        if (safe) node.setAttribute('srcset', safe)
        else node.removeAttribute('srcset')
      }

      if ((tagName === 'video' || tagName === 'audio') && node.getAttribute('src')) {
        const safe = sanitizeSrc(node.getAttribute('src'))
        if (!safe) node.setAttribute('src', '')
      }

      if (tagName === 'source' && node.getAttribute('src')) {
        const safe = sanitizeSrc(node.getAttribute('src'))
        if (!safe) node.setAttribute('src', '')
      }

      if ((tagName === 'video' || tagName === 'img') && node.getAttribute('poster')) {
        const safe = sanitizeSrc(node.getAttribute('poster'))
        if (!safe) node.setAttribute('poster', '')
      }

      if (tagName === 'track' && node.getAttribute('src')) {
        const safe = sanitizeSrc(node.getAttribute('src'))
        if (!safe) node.setAttribute('src', '')
      }

      Array.from(node.childNodes).forEach(walk)
    }
  }

  walk(tempDiv)

  return tempDiv.innerHTML
}
